import { PrototypeAccordion } from './prototype-accordion';
import { EYE_VALUES, PrototypeGroupEye } from './prototype-group-eye';
import { PrototypePageSwitch } from './prototype-page-switch';
import { PrototypePanelSide, SIDE_ATTRIBUTE } from './prototype-panel-side';
import type { PrototypeUrlState, StateOf, StateOptions } from './prototype-url-state';

export interface PrototypePanelActions<S> {
    readonly onChange: (state: S) => void;
    readonly onReset: () => void;
    readonly onMaxContent: () => void;
}

/** The prototype bar's View | Edit switch and the state side panel Edit opens. Prototype chrome only. */
export class PrototypePanel<O extends StateOptions> {
    private readonly element: HTMLElement;
    private readonly urlState: PrototypeUrlState<O>;
    private readonly form: HTMLFormElement | null;
    private readonly toggle: HTMLButtonElement | null;
    private readonly viewToggle: HTMLButtonElement | null;
    private readonly eyes: ReadonlyArray<PrototypeGroupEye>;
    private readonly onChange: (state: StateOf<O>) => void;
    private state: StateOf<O>;

    constructor(element: HTMLElement, urlState: PrototypeUrlState<O>, state: StateOf<O>, actions: PrototypePanelActions<StateOf<O>>) {
        this.element = element;
        this.urlState = urlState;
        this.form = element.querySelector('form');
        this.toggle = element.querySelector('.prototype-panel-toggle');
        this.viewToggle = element.querySelector('[data-prototype-view-toggle]');
        this.state = state;
        this.onChange = actions.onChange;

        const pageSwitch = element.querySelector<HTMLElement>('.prototype-page-switch');
        if (pageSwitch) {
            new PrototypePageSwitch(pageSwitch);
        }

        this.toggle?.addEventListener('click', () => this.setOpen(true));
        this.viewToggle?.addEventListener('click', () => this.setOpen(false));
        element.querySelector('[data-prototype-panel-close]')?.addEventListener('click', () => {
            this.setOpen(false);
            this.toggle?.focus();
        });
        element.querySelector('[data-prototype-panel-dock]')?.addEventListener('click', () => {
            const side = PrototypePanelSide.other(element.getAttribute(SIDE_ATTRIBUTE));
            PrototypePanelSide.store(side);
            element.setAttribute(SIDE_ATTRIBUTE, side);
        });
        element.setAttribute(SIDE_ATTRIBUTE, PrototypePanelSide.stored());

        this.form?.querySelectorAll<HTMLElement>('[data-prototype-group]').forEach(group => PrototypeAccordion.bind(group));
        this.eyes = Array.from(this.form?.querySelectorAll<HTMLButtonElement>('[data-prototype-group-eye]') ?? [])
            .map(button => new PrototypeGroupEye(button, (key, value) => this.pick(key, value)));
        this.form?.addEventListener('change', (e: Event) => {
            if (e.target instanceof HTMLInputElement) {
                this.pick(e.target.name, e.target.value);
            }
        });
        this.form?.addEventListener('submit', (e: Event) => e.preventDefault());
        element.querySelector('[data-prototype-action="reset"]')?.addEventListener('click', actions.onReset);
        element.querySelector('[data-prototype-action="max-content"]')?.addEventListener('click', actions.onMaxContent);

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            const target = e.target;
            const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement
                || (target instanceof HTMLElement && target.isContentEditable);
            if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey && e.key.toLowerCase() === 's') {
                this.setOpen(this.form?.hidden ?? false);
            }
        });

        this.warnUnwired();
        this.setState(state);
    }

    public setState(state: StateOf<O>) {
        this.state = state;
        for (const key of this.urlState.keys()) {
            const input = this.form?.querySelector<HTMLInputElement>(`input[name="${key}"][value="${state[key]}"]`);
            if (input) {
                input.checked = true;
            }
        }

        for (const eye of this.eyes) {
            if (this.urlState.isKey(eye.key)) {
                eye.sync(state[eye.key]);
            }
        }
    }

    /** Whether the page shows the Max content preset, which turns its header button into "back to normal". */
    public setMaxContent(on: boolean) {
        this.element.toggleAttribute('data-prototype-max-content', on);
    }

    private pick(key: string, value: string) {
        const next = this.urlState.withValue(this.state, key, value);
        if (next) {
            this.setState(next);
            this.onChange(next);
        }
    }

    private setOpen(open: boolean) {
        if (!this.form) {
            return;
        }

        this.form.hidden = !open;
        this.toggle?.setAttribute('aria-expanded', open.toString());
        this.viewToggle?.setAttribute('aria-pressed', (!open).toString());
        // A hook for design-specific edit chrome (✎ buttons on the page) that should only show in edit mode.
        document.documentElement.toggleAttribute('data-prototype-panel-open', open);
    }

    /** A state value without its radio or eye can never be picked, and a radio or eye outside the state options does nothing. */
    private warnUnwired() {
        const form = this.form;
        if (!form) {
            return;
        }

        const eyeKeys = new Set(this.eyes.map(eye => eye.key));
        for (const key of this.urlState.keys()) {
            if (eyeKeys.has(key)) {
                continue;
            }

            for (const value of this.urlState.values(key)) {
                if (!form.querySelector(`input[name="${key}"][value="${value}"]`)) {
                    console.warn(`Prototype panel: state "${key}" has no radio for "${value}".`);
                }
            }
        }

        form.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(input => {
            if (!this.urlState.isKey(input.name) || !this.urlState.allows(input.name, input.value)) {
                console.warn(`Prototype panel: radio ${input.name}=${input.value} is not in the state options.`);
            }
        });

        for (const key of eyeKeys) {
            const values: readonly string[] = this.urlState.isKey(key) ? this.urlState.values(key) : [];
            if (values.length !== EYE_VALUES.length || !EYE_VALUES.every(value => values.includes(value))) {
                console.warn(`Prototype panel: the eye on "${key}" needs a state with exactly the values ${EYE_VALUES.join(', ')}.`);
            }
        }
    }
}
